import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Target, Plus, Search, Filter, MoreVertical, Play, Pause, CheckCircle,
  TrendingUp, Users, MousePointerClick, Eye, Calendar, Tag, ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import campaignsApi from '@/api/campaigns';

const statusColors = {
  draft: 'bg-slate-500',
  scheduled: 'bg-blue-500',
  active: 'bg-emerald-500',
  paused: 'bg-amber-500',
  completed: 'bg-purple-500',
  archived: 'bg-gray-500',
};

const goalIcons = {
  awareness: Eye,
  traffic: MousePointerClick,
  leads: Users,
  engagement: TrendingUp,
  conversions: CheckCircle,
};

export default function Campaigns() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  // Card totals come from /campaigns/stats (every campaign in scope),
  // never from a reduce over the 50 rows on screen.
  const [summary, setSummary] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [goalOptions, setGoalOptions] = useState([]);

  const [newCampaign, setNewCampaign] = useState({
    name: '',
    description: '',
    goal: 'traffic',
    tags: '',
  });

  useEffect(() => {
    loadCampaigns();
    loadOptions();
  }, [statusFilter]);

  const loadOptions = async () => {
    try {
      const result = await campaignsApi.getGoalOptions();
      if (result.success) setGoalOptions(result.data);
    } catch (error) {
      console.error('Failed to load options:', error);
    }
  };

  const loadCampaigns = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const [result, stats] = await Promise.all([
        campaignsApi.list(params),
        campaignsApi.stats().catch(() => null),
      ]);
      if (result.success) {
        setCampaigns(result.data);
      }
      setSummary(stats?.success ? stats.data : null);
    } catch (error) {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newCampaign.name.trim()) {
      toast.error('Campaign name is required');
      return;
    }

    try {
      const data = {
        ...newCampaign,
        tags: newCampaign.tags ? newCampaign.tags.split(',').map(t => t.trim()) : [],
      };
      const result = await campaignsApi.create(data);
      if (result.success) {
        toast.success('Campaign created!');
        setShowCreateDialog(false);
        setNewCampaign({ name: '', description: '', goal: 'traffic', tags: '' });
        loadCampaigns();
      } else {
        toast.error(result.detail || 'Failed to create campaign');
      }
    } catch (error) {
      toast.error('Failed to create campaign');
    }
  };

  const handleActivate = async (id) => {
    try {
      const result = await campaignsApi.activate(id);
      if (result.success) {
        toast.success('Campaign activated!');
        loadCampaigns();
      }
    } catch (error) {
      toast.error('Failed to activate campaign');
    }
  };

  const handlePause = async (id) => {
    try {
      const result = await campaignsApi.pause(id);
      if (result.success) {
        toast.success('Campaign paused');
        loadCampaigns();
      }
    } catch (error) {
      toast.error('Failed to pause campaign');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to archive this campaign?')) return;
    try {
      const result = await campaignsApi.delete(id);
      if (result.success) {
        toast.success('Campaign archived');
        loadCampaigns();
      }
    } catch (error) {
      toast.error('Failed to archive campaign');
    }
  };

  const filteredCampaigns = campaigns.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const GoalIcon = ({ goal }) => {
    const Icon = goalIcons[goal] || Target;
    return <Icon className="w-4 h-4" />;
  };

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Campaigns</h1>
          <p className="text-muted-foreground mt-1">Organize your marketing initiatives</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-campaign-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Campaign
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search campaigns..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="search-campaigns"
          />
        </div>
        <Select value={statusFilter || "all"} onValueChange={(val) => setStatusFilter(val === "all" ? "" : val)}>
          <SelectTrigger className="w-40" data-testid="status-filter">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="paused">Paused</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Campaigns</p>
                <p className="text-2xl font-bold" data-testid="campaigns-total">{summary ? summary.total : '-'}</p>
              </div>
              <Target className="w-8 h-8 text-primary/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {summary ? summary.active : '-'}
                </p>
              </div>
              <Play className="w-8 h-8 text-emerald-500/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Leads</p>
                <p className="text-2xl font-bold">
                  {summary ? summary.total_leads : '-'}
                </p>
              </div>
              <Users className="w-8 h-8 text-blue-500/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Scans</p>
                <p className="text-2xl font-bold">
                  {summary ? summary.total_scans : '-'}
                </p>
              </div>
              <MousePointerClick className="w-8 h-8 text-purple-500/20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Campaigns Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-6 bg-muted rounded w-3/4 mb-4" />
                <div className="h-4 bg-muted rounded w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredCampaigns.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Target className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium mb-2">No campaigns yet</h3>
            <p className="text-muted-foreground mb-4">Create your first campaign to organize your marketing</p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Campaign
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="campaigns-grid">
          {filteredCampaigns.map(campaign => (
            <Card 
              key={campaign.id} 
              className="hover:shadow-lg transition-shadow cursor-pointer group"
              onClick={() => navigate(`/campaigns/${campaign.id}`)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={`${statusColors[campaign.status]} text-white text-xs`}>
                        {campaign.status}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        <GoalIcon goal={campaign.goal} />
                        <span className="ml-1">{campaign.goal}</span>
                      </Badge>
                    </div>
                    <CardTitle className="text-lg">{campaign.name}</CardTitle>
                    {campaign.description && (
                      <CardDescription className="line-clamp-2 mt-1">
                        {campaign.description}
                      </CardDescription>
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {campaign.status === 'draft' || campaign.status === 'paused' ? (
                        <DropdownMenuItem onClick={() => handleActivate(campaign.id)}>
                          <Play className="w-4 h-4 mr-2" /> Activate
                        </DropdownMenuItem>
                      ) : campaign.status === 'active' ? (
                        <DropdownMenuItem onClick={() => handlePause(campaign.id)}>
                          <Pause className="w-4 h-4 mr-2" /> Pause
                        </DropdownMenuItem>
                      ) : null}
                      <DropdownMenuItem onClick={() => handleDelete(campaign.id)} className="text-destructive">
                        Archive
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-2 bg-muted/50 rounded">
                    <p className="text-lg font-semibold">{campaign.stats?.total_scans || 0}</p>
                    <p className="text-xs text-muted-foreground">Scans</p>
                  </div>
                  <div className="p-2 bg-muted/50 rounded">
                    <p className="text-lg font-semibold">{campaign.stats?.total_clicks || 0}</p>
                    <p className="text-xs text-muted-foreground">Clicks</p>
                  </div>
                  <div className="p-2 bg-muted/50 rounded">
                    <p className="text-lg font-semibold">{campaign.stats?.total_leads || 0}</p>
                    <p className="text-xs text-muted-foreground">Leads</p>
                  </div>
                </div>
                {campaign.tags && campaign.tags.length > 0 && (
                  <div className="flex gap-1 mt-3 flex-wrap">
                    {campaign.tags.slice(0, 3).map(tag => (
                      <Badge key={tag} variant="secondary" className="text-xs">
                        <Tag className="w-3 h-3 mr-1" />
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Campaign</DialogTitle>
            <DialogDescription>Group your marketing assets under one initiative</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Campaign Name *</Label>
              <Input
                id="name"
                placeholder="Summer Property Showcase"
                value={newCampaign.name}
                onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                data-testid="campaign-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Campaign description..."
                value={newCampaign.description}
                onChange={(e) => setNewCampaign({ ...newCampaign, description: e.target.value })}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="goal">Campaign Goal</Label>
              <Select
                value={newCampaign.goal}
                onValueChange={(value) => setNewCampaign({ ...newCampaign, goal: value })}
              >
                <SelectTrigger data-testid="campaign-goal-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {goalOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="tags">Tags (comma-separated)</Label>
              <Input
                id="tags"
                placeholder="summer, promotion, luxury"
                value={newCampaign.tags}
                onChange={(e) => setNewCampaign({ ...newCampaign, tags: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate} data-testid="create-campaign-submit">
              Create Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
